/**
 * Rent vs Buy Calculator - Comprehensive comparison tool
 */

// Currency formatting
const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const dollarsPrecise = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

/**
 * Format currency values
 * @param {number} value - The value to format
 * @param {boolean} precise - Whether to show cents
 * @returns {string} Formatted currency string
 */
function formatCurrency(value, precise = false) {
  return precise ? dollarsPrecise.format(value) : dollars.format(value);
}

/**
 * Calculate monthly mortgage payment using amortization formula
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Annual interest rate (percentage)
 * @param {number} years - Loan term in years
 * @returns {number} Monthly payment
 */
function calculateMortgagePayment(principal, annualRate, years) {
  if (principal <= 0 || annualRate === 0) {
    return principal / (years * 12);
  }
  
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = years * 12;
  
  // M = P[r(1+r)^n]/[(1+r)^n-1]
  const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
  
  return payment;
}

/**
 * Generate amortization schedule
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Annual interest rate (percentage)
 * @param {number} years - Loan term in years
 * @param {number} monthlyPayment - Monthly payment amount
 * @returns {Array} Amortization schedule with monthly principal and interest
 */
function generateAmortizationSchedule(principal, annualRate, years, monthlyPayment) {
  const schedule = [];
  let balance = principal;
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = years * 12;
  
  for (let month = 1; month <= numPayments; month++) {
    const interestPayment = balance * monthlyRate;
    const principalPayment = monthlyPayment - interestPayment;
    balance = Math.max(0, balance - principalPayment);
    
    schedule.push({
      month: month,
      principalPayment: principalPayment,
      interestPayment: interestPayment,
      balance: balance
    });
  }
  
  return schedule;
}

/**
 * Calculate comparison data for rent vs buy
 * @param {Object} inputs - All input parameters
 * @returns {Array} Year-by-year comparison data
 */
function calculateComparison(inputs) {
  const {
    homePrice,
    downPayment,
    mortgageRate,
    loanTerm,
    propertyTaxRate,
    homeInsurance,
    hoaFees,
    maintenanceRate,
    homeAppreciation,
    closingCosts,
    monthlyRent,
    rentersInsurance,
    rentIncrease,
    investmentReturn,
    analysisYears
  } = inputs;
  
  // Calculate mortgage details
  const loanAmount = homePrice - downPayment;
  const monthlyMortgage = calculateMortgagePayment(loanAmount, mortgageRate, loanTerm);
  const amortizationSchedule = generateAmortizationSchedule(loanAmount, mortgageRate, loanTerm, monthlyMortgage);
  
  // Initialize tracking variables
  const comparisonData = [];
  
  // Buying scenario tracking
  let homeValue = homePrice;
  let mortgageBalance = loanAmount;
  
  // Renting scenario tracking
  let currentRent = monthlyRent;
  let investmentBalance = downPayment + closingCosts; // Invest what would have been down payment + closing costs
  
  // Generate year-by-year comparison
  for (let year = 0; year <= analysisYears; year++) {
    // Calculate buying costs for this year
    const yearlyPropertyTax = homeValue * (propertyTaxRate / 100);
    const yearlyMaintenance = homeValue * (maintenanceRate / 100);
    const monthlyPropertyTax = yearlyPropertyTax / 12;
    const monthlyHomeInsurance = homeInsurance / 12;
    const monthlyMaintenance = yearlyMaintenance / 12;
    
    // Total monthly cost for buying (if still paying mortgage)
    const stillPayingMortgage = year < loanTerm;
    const monthlyBuyCost = (stillPayingMortgage ? monthlyMortgage : 0) + 
                           monthlyPropertyTax + 
                           monthlyHomeInsurance + 
                           hoaFees + 
                           monthlyMaintenance;
    
    // Total monthly cost for renting
    const monthlyRentCost = currentRent + rentersInsurance;
    
    // Calculate principal paid this year (from amortization schedule)
    let principalPaidThisYear = 0;
    if (stillPayingMortgage) {
      const startMonth = year * 12;
      const endMonth = Math.min((year + 1) * 12, loanTerm * 12);
      for (let month = startMonth; month < endMonth; month++) {
        if (month < amortizationSchedule.length) {
          principalPaidThisYear += amortizationSchedule[month].principalPayment;
        }
      }
      mortgageBalance = Math.max(0, mortgageBalance - principalPaidThisYear);
    }
    
    // Calculate home equity (home value - mortgage balance)
    const homeEquity = homeValue - mortgageBalance;
    
    // Calculate monthly savings/deficit for renting scenario
    const monthlySavings = monthlyBuyCost - monthlyRentCost;
    
    // Invest the monthly difference (if renting is cheaper) or withdraw from investments (if buying is cheaper)
    // This happens monthly throughout the year
    for (let month = 0; month < 12; month++) {
      // Apply monthly investment return
      const monthlyInvestmentRate = investmentReturn / 100 / 12;
      investmentBalance = investmentBalance * (1 + monthlyInvestmentRate);
      
      // Add or subtract monthly difference
      investmentBalance += monthlySavings;
      investmentBalance = Math.max(0, investmentBalance); // Can't go negative
    }
    
    // Calculate net worth for both scenarios
    const netWorthBuy = homeEquity;
    const netWorthRent = investmentBalance;
    const netWorthDifference = netWorthBuy - netWorthRent;
    
    // Store year data
    comparisonData.push({
      year: year,
      // Buying data
      monthlyBuyCost: monthlyBuyCost,
      homeValue: homeValue,
      mortgageBalance: mortgageBalance,
      homeEquity: homeEquity,
      principalPaidThisYear: principalPaidThisYear,
      // Renting data
      monthlyRentCost: monthlyRentCost,
      investmentBalance: investmentBalance,
      // Comparison
      netWorthBuy: netWorthBuy,
      netWorthRent: netWorthRent,
      netWorthDifference: netWorthDifference
    });
    
    // Update for next year (if not the last year)
    if (year < analysisYears) {
      // Appreciate home value
      homeValue = homeValue * (1 + homeAppreciation / 100);
      
      // Increase rent
      currentRent = currentRent * (1 + rentIncrease / 100);
    }
  }
  
  return comparisonData;
}

/**
 * Find break-even year (when buying becomes better than renting)
 * @param {Array} comparisonData - Year-by-year comparison data
 * @returns {number} Break-even year, or -1 if never breaks even
 */
function findBreakEvenYear(comparisonData) {
  for (let i = 0; i < comparisonData.length; i++) {
    if (comparisonData[i].netWorthBuy > comparisonData[i].netWorthRent) {
      return comparisonData[i].year;
    }
  }
  return -1; // Never breaks even
}

/**
 * Update summary cards with comparison results
 * @param {Array} comparisonData - Year-by-year comparison data
 * @param {Object} inputs - Input parameters
 */
function updateSummaryCards(comparisonData, inputs) {
  const finalYear = comparisonData[comparisonData.length - 1];
  
  // Calculate total costs over the period
  let totalCostBuy = inputs.downPayment + inputs.closingCosts;
  let totalCostRent = 0;
  let totalMonthlyCostBuy = 0;
  let totalMonthlyCostRent = 0;
  
  for (let i = 0; i < comparisonData.length - 1; i++) { // Exclude year 0
    const yearData = comparisonData[i + 1];
    const yearlyBuyCost = yearData.monthlyBuyCost * 12;
    const yearlyRentCost = yearData.monthlyRentCost * 12;
    totalCostBuy += yearlyBuyCost;
    totalCostRent += yearlyRentCost;
    totalMonthlyCostBuy += yearData.monthlyBuyCost;
    totalMonthlyCostRent += yearData.monthlyRentCost;
  }
  
  // Calculate averages
  const avgMonthlyBuy = totalMonthlyCostBuy / inputs.analysisYears;
  const avgMonthlyRent = totalMonthlyCostRent / inputs.analysisYears;
  const monthlyDifference = avgMonthlyBuy - avgMonthlyRent;
  
  // Update monthly cost cards
  document.getElementById('avgMonthlyBuy').textContent = formatCurrency(avgMonthlyBuy);
  document.getElementById('avgMonthlyRent').textContent = formatCurrency(avgMonthlyRent);
  document.getElementById('monthlyDiff').textContent = formatCurrency(Math.abs(monthlyDifference));
  
  // Color code monthly difference
  const monthlyDiffCard = document.getElementById('monthlyDiff').parentElement;
  if (monthlyDifference > 0) {
    monthlyDiffCard.querySelector('.summary-card-label').textContent = 'Buy Costs More/Month';
    monthlyDiffCard.classList.add('rent-card');
    monthlyDiffCard.classList.remove('buy-card');
  } else {
    monthlyDiffCard.querySelector('.summary-card-label').textContent = 'Rent Costs More/Month';
    monthlyDiffCard.classList.add('buy-card');
    monthlyDiffCard.classList.remove('rent-card');
  }
  
  // Update total investment cards
  document.getElementById('totalCostBuy').textContent = formatCurrency(totalCostBuy);
  document.getElementById('totalCostRent').textContent = formatCurrency(totalCostRent);
  
  // Update asset cards
  document.getElementById('homeValue').textContent = formatCurrency(finalYear.homeValue);
  document.getElementById('mortgageBalance').textContent = formatCurrency(finalYear.mortgageBalance);
  document.getElementById('homeEquity').textContent = formatCurrency(finalYear.homeEquity);
  document.getElementById('investmentValue').textContent = formatCurrency(finalYear.investmentBalance);
  
  // Update net worth cards
  document.getElementById('netWorthBuy').textContent = formatCurrency(finalYear.netWorthBuy);
  document.getElementById('netWorthRent').textContent = formatCurrency(finalYear.netWorthRent);
  
  const difference = finalYear.netWorthBuy - finalYear.netWorthRent;
  const diffElement = document.getElementById('netWorthDiff');
  diffElement.textContent = formatCurrency(Math.abs(difference));
  
  // Color code the difference and update label
  const diffCard = diffElement.parentElement;
  if (Math.abs(difference) < 1000) {
    diffCard.querySelector('.summary-card-label').textContent = 'Nearly Equal';
    diffCard.classList.remove('buy-card', 'rent-card');
    diffCard.classList.add('difference-card');
  } else if (difference > 0) {
    diffCard.querySelector('.summary-card-label').textContent = 'Buying Advantage';
    diffCard.classList.remove('rent-card');
    diffCard.classList.add('buy-card');
  } else {
    diffCard.querySelector('.summary-card-label').textContent = 'Renting Advantage';
    diffCard.classList.remove('buy-card');
    diffCard.classList.add('rent-card');
  }
  
  // Find and display break-even year
  const breakEvenYear = findBreakEvenYear(comparisonData);
  const breakEvenElement = document.getElementById('breakEven');
  if (breakEvenYear > 0) {
    breakEvenElement.textContent = `Year ${breakEvenYear}`;
  } else if (breakEvenYear === 0) {
    breakEvenElement.textContent = 'Immediate';
  } else {
    breakEvenElement.textContent = 'Never';
  }
  
  // Update winner banner
  const winnerBanner = document.getElementById('winnerBanner');
  const winnerText = document.getElementById('winnerText');
  
  if (Math.abs(difference) < 1000) {
    winnerText.textContent = 'It\'s a tie! Both options are nearly equivalent.';
  } else if (difference > 0) {
    winnerText.textContent = `Buying is better by ${formatCurrency(difference)} after ${inputs.analysisYears} years`;
  } else {
    winnerText.textContent = `Renting is better by ${formatCurrency(Math.abs(difference))} after ${inputs.analysisYears} years`;
  }
  
  winnerBanner.classList.remove('hidden');
  
  // Show summary cards
  document.getElementById('summaryCards').classList.remove('hidden');
}

/**
 * Build and populate the comparison table
 * @param {Array} comparisonData - Year-by-year comparison data
 */
function buildComparisonTable(comparisonData) {
  const tableBody = document.getElementById('tableBody');
  const resultsTable = document.getElementById('resultsTable');
  
  tableBody.innerHTML = ''; // Clear existing rows
  
  comparisonData.forEach(row => {
    const tr = document.createElement('tr');
    
    // Determine which scenario is better for this year
    const buyBetter = row.netWorthBuy > row.netWorthRent;
    const differenceClass = buyBetter ? 'buy-value' : 'rent-value';
    const differenceSign = buyBetter ? '+' : '';
    
    tr.innerHTML = `
      <td class="year-column">${row.year}</td>
      <td class="group-divider buy-value">${formatCurrency(row.monthlyBuyCost, true)}</td>
      <td class="rent-value">${formatCurrency(row.monthlyRentCost, true)}</td>
      <td class="group-divider positive">${formatCurrency(row.homeValue)}</td>
      <td class="negative">${formatCurrency(row.mortgageBalance)}</td>
      <td class="positive">${formatCurrency(row.homeEquity)}</td>
      <td class="group-divider positive">${formatCurrency(row.investmentBalance)}</td>
      <td class="group-divider buy-value">${formatCurrency(row.netWorthBuy)}</td>
      <td class="rent-value">${formatCurrency(row.netWorthRent)}</td>
      <td class="group-divider ${differenceClass}">${differenceSign}${formatCurrency(Math.abs(row.netWorthDifference))}</td>
    `;
    
    tableBody.appendChild(tr);
  });
  
  // Show the table
  resultsTable.classList.add('show');
}

/**
 * Show error message
 * @param {string} message - Error message to display
 */
function showError(message) {
  const errorElement = document.getElementById('errorMessage');
  errorElement.textContent = message;
  errorElement.classList.remove('hidden');
}

/**
 * Clear error message
 */
function clearError() {
  const errorElement = document.getElementById('errorMessage');
  errorElement.textContent = '';
  errorElement.classList.add('hidden');
}

/**
 * Validate inputs
 * @param {Object} inputs - Input parameters to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
function validateInputs(inputs) {
  if (inputs.downPayment > inputs.homePrice) {
    return 'Down payment cannot exceed home price';
  }
  
  if (inputs.downPayment < 0) {
    return 'Down payment cannot be negative';
  }
  
  if (inputs.homePrice <= 0) {
    return 'Home price must be greater than zero';
  }
  
  if (inputs.monthlyRent <= 0) {
    return 'Monthly rent must be greater than zero';
  }
  
  if (inputs.analysisYears < 1 || inputs.analysisYears > 50) {
    return 'Analysis period must be between 1 and 50 years';
  }
  
  return null;
}

/**
 * Initialize the calculator
 */
function initializeCalculator() {
  const form = document.getElementById('calculatorForm');
  
  form.addEventListener('submit', function(e) {
    e.preventDefault();
    
    // Clear any previous errors
    clearError();
    
    // Gather inputs
    const inputs = {
      homePrice: parseFloat(document.getElementById('homePrice').value) || 0,
      downPayment: parseFloat(document.getElementById('downPayment').value) || 0,
      mortgageRate: parseFloat(document.getElementById('mortgageRate').value) || 0,
      loanTerm: parseInt(document.getElementById('loanTerm').value) || 30,
      propertyTaxRate: parseFloat(document.getElementById('propertyTaxRate').value) || 0,
      homeInsurance: parseFloat(document.getElementById('homeInsurance').value) || 0,
      hoaFees: parseFloat(document.getElementById('hoaFees').value) || 0,
      maintenanceRate: parseFloat(document.getElementById('maintenanceRate').value) || 0,
      homeAppreciation: parseFloat(document.getElementById('homeAppreciation').value) || 0,
      closingCosts: parseFloat(document.getElementById('closingCosts').value) || 0,
      monthlyRent: parseFloat(document.getElementById('monthlyRent').value) || 0,
      rentersInsurance: parseFloat(document.getElementById('rentersInsurance').value) || 0,
      rentIncrease: parseFloat(document.getElementById('rentIncrease').value) || 0,
      investmentReturn: parseFloat(document.getElementById('investmentReturn').value) || 0,
      analysisYears: parseInt(document.getElementById('analysisYears').value) || 30
    };
    
    // Validate inputs
    const validationError = validateInputs(inputs);
    if (validationError) {
      showError(validationError);
      return;
    }
    
    // Calculate comparison
    const comparisonData = calculateComparison(inputs);
    
    // Update UI
    updateSummaryCards(comparisonData, inputs);
    buildComparisonTable(comparisonData);
  });
  
  // Calculate on page load with default values
  form.dispatchEvent(new Event('submit'));
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCalculator);
} else {
  initializeCalculator();
}
